# F53b §F — Audit log as its own /audit module.
# Route: /audit (lifted out of /admin/audit per canonical-deliverables).
# Audience: Quality, Supervisor, Admin (RequirePermission AuditLog:Read).
#
# Filter UX (F75 R13): the canonical list-page filter bar — the same shape and
# behaviour as the tracking record list (record-list-filters). Real `Input`
# controls bound to the `filters` model emit `xp85-filter-changed` on each
# edit, which the generated page re-fetches `entries` against (debounced); the
# Apply button forces an immediate re-fetch and Clear resets the bar. The
# filter field names (entityType, userId, action, from, to) match the
# GET /api/audit query parameters 1:1, and the AuditQuery data provider's
# `forward_all` passes them straight through to QueryAuditEntries — the single
# CIR-field == query-param == endpoint-parameter contract used by the tracking
# entry list (EntryFilterForm).
#
# Presentation (F75) mirrors the tracking record list (the canonical list-page
# shape, see docs/internal/guides/developers/design-system/patterns.md § List & filter):
# elevated rounded filter bar, table-style header row + dense ruled rows,
# pagination below the rows.

page-audit-trail:
  type: Page
  archetype: audit
  meta:
    title: $t.audit_trail.meta_title
    auth: required
  data:
    # Entries query — backed by GET /api/audit with filter query string. The
    # generated page mirrors the `filters` model into the request and re-fetches
    # when any filter Input changes (xp85-filter-changed), exactly like the
    # tracking record list.
    # Bind to the AuditQuery model (NOT AuditEntry): AuditEntry resolves to the
    # per-entity route GET /api/audit/{entityId} (empty id → /api/audit/, the
    # wrong endpoint). AuditQuery resolves to the filter-driven list route
    # GET /api/audit?{filters} (QueryAuditEntries) with the same row shape.
    entries:
      model: AuditQuery[]
      # F75 #131 — fetch a deep window (server clamps to 1000) so the
      # pages-mode pagination on audit-entries-list has real depth instead
      # of the 100-row server default. maxCount is not a filter dimension
      # (AuditQueryFilter.HasAnyFilter ignores it), so the endpoint's
      # last-24h default window still applies when no filter fields are set.
      # Seeding it here puts it in the page's _queryState so it survives every
      # filter-driven re-fetch.
      filter:
        maxCount: 500
      refresh: on_mount
      data_states:
        loading:
          skeleton_count: 5
        empty:
          ref: audit-entries-empty
        error: default
    # Filter state — the source of truth the filter bar binds to and the Apply
    # button forwards as query params. Empty strings are dropped client-side, so
    # a blank field is not sent and the endpoint's last-24h default window
    # applies when no field is set.
    filters:
      model: AuditFilterForm
      initial:
        entityType: ""
        userId: ""
        action: ""
        from: ""
        to: ""
  spatial:
    display: flex
    direction: column
    gap: $spacing.xl
    padding: $spacing.lg
    padding_inline: $spacing.xl
    overflow: auto
    responsive:
      sm:
        padding: $spacing.sm
        padding_inline: $spacing.sm
        gap: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface
  children:
    - audit-page-header
    - audit-filters
    - audit-table

# ── Page header ──────────────────────────────────────────────────────────────

audit-page-header:
  type: PageHeader
  content: $t.audit_trail.title

# ── Filter bar (F75 R13) ─────────────────────────────────────────────────────
# Same shape and behaviour as the tracking record-list filter bar
# (record-list-filters): an elevated rounded row of Input controls bound to the
# `filters` model, with Apply + Clear actions. Each Input emits
# `xp85-filter-changed` on edit, which the generated page maps into its query
# state and re-fetches `entries` against — so typing a filter narrows the
# results live, and Apply forces an immediate re-fetch.
#
# entityType / userId / action are free-text exact-match strings (the backend
# QueryAuditEntries compares them with `=`), so a text Input is the correct
# control — there is no fixed enum of entity types or action names. from / to
# are datetime bounds (occurred_at >= / <=).

audit-filters:
  type: Section
  a11y:
    role: toolbar
    label: $t.audit_trail.filter_bar_label
  spatial:
    display: flex
    gap: $spacing.md
    padding: $spacing.md
    direction: row
    align: flex-end
    wrap: wrap
  tokens:
    background: $color_scheme.light.color.surface-elevated
    radius: $radius.lg
    shadow: $color_scheme.light.shadow.sm
  children:
    - audit-filter-entity-type
    - audit-filter-user
    - audit-filter-action
    - audit-filter-from
    - audit-filter-to
    - audit-filter-apply
    - audit-filter-clear

audit-filter-entity-type:
  type: Input
  input_type: text
  label: $t.audit_trail.filter_entity_type
  placeholder: $t.audit_trail.filter_entity_type_placeholder
  bind: filters.entityType
  a11y:
    label: $t.audit_trail.filter_entity_type_label
  spatial:
    flex: 1
    min_width: 10rem

audit-filter-user:
  type: Input
  input_type: text
  label: $t.audit_trail.filter_user
  placeholder: $t.audit_trail.filter_user_placeholder
  bind: filters.userId
  a11y:
    label: $t.audit_trail.filter_user_label
  spatial:
    flex: 1
    min_width: 10rem

audit-filter-action:
  type: Input
  input_type: text
  label: $t.audit_trail.filter_action
  placeholder: $t.audit_trail.filter_action_placeholder
  bind: filters.action
  a11y:
    label: $t.audit_trail.filter_action_label
  spatial:
    flex: 1
    min_width: 8rem

audit-filter-from:
  type: Input
  input_type: datetime
  label: $t.audit_trail.filter_from
  bind: filters.from
  a11y:
    label: $t.audit_trail.filter_from_label
  spatial:
    flex: 1
    min_width: 10rem

audit-filter-to:
  type: Input
  input_type: datetime
  label: $t.audit_trail.filter_to
  bind: filters.to
  a11y:
    label: $t.audit_trail.filter_to_label
  spatial:
    flex: 1
    min_width: 10rem

audit-filter-apply:
  type: Button
  content: $t.audit_trail.btn_apply
  variant: outlined
  color: info
  a11y:
    label: $t.audit_trail.btn_apply_label
  action:
    type: custom
    data_source: entries
    params: filters
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.lg

audit-filter-clear:
  type: Button
  variant: ghost
  content: $t.common.filter_clear
  a11y:
    label: $t.common.filter_clear_label
  action:
    type: custom
  spatial:
    min_height: $size.touch-target
    padding_inline: $spacing.md

# ── Audit entries table (F75 #130 + #131, tracking-aligned) ──────────────────
#
# Table-style presentation mirroring the tracking record-list Table: a bold
# header row directly on top of dense, hairline-ruled rows that share one
# grid column template — When · Who · What · Affected. Composed from List +
# grid rows (not the Table node) because this screen pages CLIENT-side over
# the window it already fetched, with a row template per entry: a Table's
# `pagination:` pages the SERVER query of a page data source instead, and the
# Table node has no row template and emits no per-row node ids. The wrapper Section
# stacks header + rows flush (gap 0) so they read as one table; the row
# count lives in the pagination's "Showing X–Y of Z" status line.
#
# Pagination (#131): pages-mode pagination on the List node — Previous/Next +
# numbered page buttons + "Showing X–Y of Z" status + aria-live "Page N of M
# loaded" announcements, paging the fetched window client-side (the deep
# maxCount fetch above).
#
# Read-model fields: userDisplayName (Who), summary (What), entityTypeLabel + entityLabel
# (Affected); the summary and the type label read in the caller's language, while the Entity
# type filter matches the recorded identifier. The What cell is a ghost button that
# opens the entry's detail page (/audit/entry/:auditId) — raw identifiers and
# the tamper-evident hash live there (page-audit-detail), the single canonical
# home for per-entry integrity data.

audit-table:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: 0rem
  children:
    - audit-table-header
    - audit-entries-list

# Header row — mirrors the emitted Table <th> treatment: sm font, 600 weight,
# default on-surface color, cell padding identical to the rows below so the
# grid columns align. The header and every row are SEPARATE grids, so they align
# only while no cell can widen its own track: each track is minmax(0, Nfr), whose
# zero minimum keeps a long cell (the nowrap extension badge) from growing the
# track on its row alone. A bare `1fr` has an `auto` minimum and would.
audit-table-header:
  type: Section
  spatial:
    display: grid
    columns: "minmax(0,2fr) minmax(0,1fr) minmax(0,2fr) minmax(0,1fr)"
    gap: $spacing.md
    padding_block: $spacing.sm
    padding_inline: $spacing.md
  tokens:
    background: $color_scheme.light.color.surface-elevated
    border: $border.default
  children:
    - audit-th-when
    - audit-th-who
    - audit-th-what
    - audit-th-affected

audit-th-when:
  type: Text
  content: $t.audit_trail.col_when
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-th-who:
  type: Text
  content: $t.audit_trail.col_who
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-th-what:
  type: Text
  content: $t.audit_trail.col_what
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-th-affected:
  type: Text
  content: $t.audit_trail.col_affected
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

audit-entries-list:
  type: List
  each: $data.entries
  template: audit-entry-row
  empty: audit-entries-empty
  pagination:
    mode: pages
    page_size: 20
  a11y:
    label: $t.audit_trail.entries_label
  spatial:
    display: flex
    direction: column
    gap: 0rem

# Table-style row — IDENTICAL column template + cell padding to
# audit-table-header so cells align. Rows stack flush (List gap 0); each
# row's hairline border supplies the table ruling.
audit-entry-row:
  type: Card
  spatial:
    display: grid
    columns: "minmax(0,2fr) minmax(0,1fr) minmax(0,2fr) minmax(0,1fr)"
    gap: $spacing.md
    padding_block: $spacing.sm
    padding_inline: $spacing.md
    align: center
  tokens:
    background: $color_scheme.light.color.surface
    border: $border.default
  children:
    - audit-row-when
    - audit-row-who
    - audit-row-what
    - audit-cell-affected

# When — audit-grade second-precision timestamp (21 CFR Part 11 — the
# regulated audit trail keeps the full precise stamp visible in the cell;
# the µs export stamp lives on the detail page). The tracking table renders
# `datetime-short` because Table columns pair it with an automatic
# compliance-datetime title tooltip; that tooltip pairing is Table-column
# emitter behaviour only, so this List-row Text cell keeps the full
# compliance-datetime. When the emitter extends the lossy-format tooltip to
# formatted Text/bind nodes, switch `format:` below to datetime-short.
audit-row-when:
  type: Text
  format: compliance-datetime
  bind:
    content: item.timestamp
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface

# Who — display name from the read-model (#130), linking to the user's
# profile page — the same treatment as the detail page's Who row (M1
# audit-trail-ux-hardening), so the two views don't diverge. `$data.userId`
# (not `item.userId`) matches the row-navigate convention this same list
# already uses on the What cell below (`route: /audit/entry/$data.auditId`):
# inside a List row's per-row component, `$data.<field>` addresses the row's
# own fields directly.
audit-row-who:
  type: Button
  variant: ghost
  bind:
    content: item.userDisplayName
  a11y:
    label: $t.audit_trail.col_user
  action:
    type: navigate
    route: /admin/users/$data.userId
  tokens:
    font_size: $font.size.sm
    padding_inline: 0

# What — humanized action phrase as a ghost button that opens the entry's
# detail page. A Button is the only keyboard-accessible emitter-supported
# row navigation inside a paginated List row (the Table row_action is
# ignored in pages-mode and List rows carry no implicit click handler).
audit-row-what:
  type: Button
  variant: ghost
  bind:
    content: item.summary
  a11y:
    label: $t.audit_trail.row_open_label
  action:
    type: navigate
    route: /audit/entry/$data.auditId
    # Capture this (possibly filtered) /audit list as the return origin so the
    # entry detail's hierarchical Back returns to the EXACT filtered list — the
    # emitter's origin_only now preserves the filter query (Act 3.4).
    return_context: origin_only

# Affected — entity type + human entity label from the read-model (null label
# → empty node; the raw entityId lives on the detail page), and, on a row whose
# entityId is source text or on a row that records a script that ran, the badge
# that says so.
#
# A null label has TWO causes, and only one of them is an absence. The first is
# an entity type with no label source and an opaque GUID id — nothing informative to
# show. The second is an entityId that is source text: AuditEndpoints.MapToDtosAsync
# deliberately withholds the entityId fallback for those rows, because their
# entityId is the first 200 characters of an evaluated expression's SOURCE text — and
# this cell would render it at truncate(40) under a heading that says
# "Affected" — a reviewer would read a fragment of code as the record acted upon,
# which is the misreading the detail page's note exists to prevent. The executed text
# is on the detail page under "Entity ID" with the note that says what it holds.
#
# The entityType cell alone does not say what the entity ID holds: "Expression" reads as
# one more entity type among ItemLot and TrackingRecord, and the producer may supply its
# own entity type, so a reader scanning the list cannot tell a row whose entity ID is
# source text from one whose ID identifies a record. `audit-row-source-text-id-badge`
# marks the first, and it is keyed on the server-computed `entityIdIsSourceText` — the
# predicate `AuditEntry.EntityIdIsSourceText` keys on the ACTION (`ExpressionEvaluated`),
# never on the entity type — so the row and the detail page's note cannot disagree about
# which rows they mean. A "Script" row is the other case: its entity ID is the script's
# identity, `<definition>@<version>#<function>`, which names what ran, so this cell shows
# it as the row's label — and under the "Affected" heading that label would read as a
# record acted upon. `audit-row-script-execution-badge` marks it, keyed on the
# server-computed `isScriptExecution` (`AuditEntry.IsScriptExecution`, the action
# `ScriptExecuted`). The two flags are disjoint, so a row carries at most one badge.
# A script row's label is shown whole by `audit-row-affected-script`: a device script
# reads `<class name> v<version> · <slot>` (the server resolves the class id to its
# name), and cutting it at 40 characters drops the slot, which is the part that says
# which script ran. It wraps at its spaces and, inside a slot, anywhere.
audit-cell-affected:
  type: Section
  spatial:
    display: flex
    direction: column
    gap: $spacing.xs
    align: flex-start
    # The track is minmax(0, 1fr), so content wider than it is clipped at the
    # cell edge rather than spilling into the next row's column; the badge
    # carries its full text as a tooltip.
    overflow: hidden
  children:
    - audit-row-affected-type
    - audit-row-source-text-id-badge
    - audit-row-script-execution-badge
    - audit-row-affected-id
    - audit-row-affected-script

audit-row-affected-type:
  type: Text
  bind:
    content: item.entityTypeLabel
  a11y:
    label: $t.audit_trail.col_entity_type
  tokens:
    font_size: $font.size.sm
    font_weight: "600"
    color: $color_scheme.light.color.on-surface

# Source-text-ID discriminator (Feature #1556): marks the row whose entity ID is the
# executed source text rather than a record identifier. A TEXT badge, so the row is told
# apart by what it says and never by colour alone; `info` is the neutral category tone
# (colour encodes state, never mere category — the record list's context-type badge
# uses it for the same reason). It sits in the Affected cell, the one cell an
# such row leaves empty by design, so it costs the grid no column. A
# Badge does not wrap (nowrap pill); where the Affected track is narrower than the
# label, the cell clips it and the tooltip carries the full text.
#
# THE PREDICATE HEAD IS `data.`, AND IT HAS TO BE. Inside a List row, the List hands
# each row its item as `.data`, and every descendant of the template is handed the
# same object (EmitContext.IsTemplateChild), so `data.entityIdIsSourceText` compiles to
# `this.data?.entityIdIsSourceText` — the row's own flag. The detail page's
# `entry.entityIdIsSourceText` names a data SOURCE; written here it would compile to
# `this.data?.entry?.entityIdIsSourceText`, which is undefined on every row, and the
# badge would never mount while the emit stayed clean.
# `AuditTrailSourceTextIdBadgeTests` holds both.
audit-row-source-text-id-badge:
  type: Badge
  variant: info
  content: $t.audit_trail.badge_entity_id_is_source_text
  tooltip: $t.audit_trail.badge_entity_id_is_source_text
  visible_when: data.entityIdIsSourceText == true

# Script-execution marker: the row records a script that ran, and the label under it
# is the script's identity, not a record. Same shape and same `data.` head as the
# source-text badge above, for the same reasons; `info` tone, text not colour.
# `AuditTrailSourceTextIdBadgeTests` holds it on its own flag.
audit-row-script-execution-badge:
  type: Badge
  variant: info
  content: $t.audit_trail.badge_script_execution
  tooltip: $t.audit_trail.badge_script_execution
  visible_when: data.isScriptExecution == true

audit-row-affected-id:
  type: Text
  format: truncate(40)
  visible_when: data.isScriptExecution == false
  bind:
    content: item.entityLabel
  a11y:
    label: $t.audit_trail.col_entity_label
  tokens:
    font_size: $font.size.xs
    color: $color_scheme.light.color.on-surface-muted

audit-row-affected-script:
  type: Text
  visible_when: data.isScriptExecution == true
  bind:
    content: item.entityLabel
  a11y:
    label: $t.audit_trail.col_entity_label
  tokens:
    font_size: $font.size.xs
    color: $color_scheme.light.color.on-surface-muted
    # A slot such as `commands[read_stable].script` has no space to break at, and the
    # cell clips what overflows its track.
    overflow_wrap: anywhere

audit-entries-empty:
  type: EmptyState
  content: $t.audit_trail.empty
  body: $t.audit_trail.empty_body
  tokens:
    font_size: $font.size.sm
    color: $color_scheme.light.color.on-surface-muted
    padding: $spacing.lg
